import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { core } from "@/lib/mh";
import { errText , copyText } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Panel, Eyebrow, timeAgo } from "@/components/mh/bits";
import { Input, Btn, Copyable } from "@/components/mh/controls";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const inviteUrl = (t) => `${window.location.origin}/invite/${t}`;

const Members = ({ ws }) => {
  const { isOwner } = useAuth();
  const [data, setData] = useState({ members: [], invites: [] });
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("manager");
  const [last, setLast] = useState(null);
  const load = useCallback(() => core.members(ws.id).then((r) => setData(r.data)).catch(() => {}), [ws.id]);
  useEffect(() => { load(); setLast(null); }, [load]);
  const invite = async (e) => {
    e.preventDefault();
    try { const { data: inv } = await core.invite(ws.id, { email, role }); setLast(inv); setEmail(""); load(); toast.success("Invite link ready to copy"); } catch (e2) { toast.error(errText(e2)); }
  };
  return (
    <Panel cls="p-5 space-y-5" testId="members-panel">
      <div><Eyebrow>Team · {ws.name}</Eyebrow><h3 className="font-display text-2xl mt-1">Who can see and steer this workspace</h3></div>
      <ul className="divide-y divide-white/[0.06]">
        {data.members.map((m) => (
          <li key={m.id} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`member-${m.email}`}>
            <span className="flex-1 truncate">{m.name} <span className="text-muted-foreground">· {m.email}</span></span>
            <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">{m.role}</span>
            {isOwner && <button className="text-xs text-[#FF3B5C] hover:underline" onClick={() => core.removeMember(ws.id, m.id).then(load)} data-testid={`remove-member-${m.email}`}>Remove</button>}
          </li>
        ))}
        {data.members.length === 0 && <li className="py-3 text-sm text-muted-foreground">No members yet. Invite the client's owner below.</li>}
      </ul>
      {isOwner && (
        <form onSubmit={invite} className="grid sm:grid-cols-[1fr_160px_auto] gap-2 items-end">
          <Input label="Invite by email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} testId="invite-email" />
          <Select value={role} onValueChange={setRole}>
            <SelectTrigger className="h-10 rounded-lg bg-white/[0.03] border-white/10" data-testid="invite-role"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#0A0A0F] border-white/10"><SelectItem value="owner">Owner</SelectItem><SelectItem value="manager">Manager</SelectItem><SelectItem value="viewer">Viewer</SelectItem></SelectContent>
          </Select>
          <Btn variant="primary" type="submit" testId="invite-submit">Create invite</Btn>
        </form>
      )}
      {last && <Copyable label={`Invite link for ${last.email} (send it yourself; no email goes out yet)`} value={inviteUrl(last.token)} testId="invite-link" onCopy={() => toast.success("Copied")} />}
      {data.invites.length > 0 && (
        <div><Eyebrow cls="mb-2">Pending invites</Eyebrow>
          <ul className="space-y-1.5">{data.invites.map((i) => (
            <li key={i.token} className="flex items-center gap-3 text-sm"><span className="flex-1 truncate">{i.email} <span className="text-muted-foreground">· {i.role}</span></span>
              <button className="text-xs underline" onClick={() => { copyText(inviteUrl(i.token)); toast.success("Copied"); }}>Copy link</button>
              <button className="text-xs text-[#FF3B5C]" onClick={() => core.revokeInvite(ws.id, i.token).then(load)}>Revoke</button></li>
          ))}</ul>
        </div>
      )}
    </Panel>
  );
};

export default function Workspaces() {
  const { isAdmin, current, switchWorkspace, reload } = useAuth();
  const [list, setList] = useState([]);
  const [reqs, setReqs] = useState([]);
  const [sel, setSel] = useState(null);
  const [name, setName] = useState("");
  const [demo, setDemo] = useState(false);
  const load = useCallback(() => { if (isAdmin) { core.workspaces().then((r) => setList(r.data)); core.requests().then((r) => setReqs(r.data)); } }, [isAdmin]);
  useEffect(() => { load(); }, [load]);
  const create = async (e) => {
    e.preventDefault();
    try { const { data } = await core.createWorkspace({ name, demo_activity: demo }); toast.success(`${data.name} created`); setName(""); load(); reload(); setSel(data); } catch (e2) { toast.error(errText(e2)); }
  };
  const target = sel || current;

  return (
    <div className="max-w-[1300px] mx-auto space-y-6" data-testid="workspaces-page">
      <header className="mh-rise">
        <Eyebrow>{isAdmin ? "Agency admin" : "Workspace"}</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">{isAdmin ? <>Every client, <em className="text-muted-foreground">one house.</em></> : <>Your team.</>}</h1>
      </header>
      {isAdmin && (
        <div className="grid lg:grid-cols-[1fr_380px] gap-4">
          <Panel cls="p-5" testId="workspace-list">
            <Eyebrow>Client workspaces · {list.length}</Eyebrow>
            <ul className="mt-3 divide-y divide-white/[0.06]">
              {list.map((w) => (
                <li key={w.id} className={`py-3 flex flex-wrap items-center gap-3 ${target?.id === w.id ? "text-foreground" : ""}`} data-testid={`workspace-row-${w.slug}`}>
                  <button className="flex-1 min-w-0 text-left" onClick={() => setSel(w)}><div className="font-medium truncate">{w.name}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">model {w.model_mode} · runtime {w.runtime_mode} · {w.members} members · {timeAgo(w.created_at)}</div></button>
                  <Btn onClick={() => switchWorkspace(w.id)} testId={`open-workspace-${w.slug}`}>Open</Btn>
                </li>
              ))}
            </ul>
          </Panel>
          <Panel cls="p-5" testId="create-workspace">
            <Eyebrow>New client workspace</Eyebrow>
            <form onSubmit={create} className="space-y-3 mt-3">
              <Input label="Client name" required minLength={2} value={name} onChange={(e) => setName(e.target.value)} testId="new-workspace-name" />
              <label className="flex items-center gap-2 text-sm text-muted-foreground"><input type="checkbox" checked={demo} onChange={(e) => setDemo(e.target.checked)} className="accent-[#D4FF3A]" data-testid="new-workspace-demo" />Start with demo activity</label>
              <p className="text-xs text-muted-foreground">Starts on Managed model and Managed runtime. Switch either one in Connections.</p>
              <Btn variant="primary" type="submit" testId="create-workspace-btn">Create workspace</Btn>
            </form>
          </Panel>
        </div>
      )}
      {target && <Members ws={target} />}
      {isAdmin && reqs.length > 0 && (
        <Panel cls="p-5" testId="workspace-requests">
          <Eyebrow>Workspace requests · {reqs.length}</Eyebrow>
          <ul className="mt-3 divide-y divide-white/[0.06]">{reqs.map((r) => (
            <li key={r.id} className="py-2.5 text-sm"><span className="font-medium">{r.name}</span> <span className="text-muted-foreground">· {r.email} · {r.company}</span>
              {r.audit_id && <a href={`/report/${r.audit_id}`} className="ml-2 underline text-xs">audit</a>}<div className="text-xs text-muted-foreground">{r.message}</div></li>
          ))}</ul>
        </Panel>
      )}
    </div>
  );
}
